import React, { useEffect, useState } from "react";
import api from "config/axiosConfig";
import Modal from "ui/Modal";
import ScrollableTabsList from "ui/ScrollableTabsList";
import LoadingSkeleton from "ui/LoadingSkeleton";
import EntityAvatar from "components/Entities/EntityAvatar";
import EntitySubscription from "ui/EntitySubscription";
import { useUser } from "util/UserContext";
import { Issue } from "util/constants";
import { getIssueRankings, IssueRanking } from "util/userUtils";
import { useNavigate } from "react-router-dom";
import { apiRoutes } from "util/apiRoutes";
import EmptyState from "ui/EmptyState";
import { Bell } from "lucide-react";

interface SubscriptionRelation {
  rel_node_type: string;
  rel_node_id: number;
  rel_type: string;
  properties?: {
    value?: number;
    privacy?: string;
  };
}

interface SubscriptionResponse {
  relations: SubscriptionRelation[];
}

interface IssuesCountResponse {
  total: number;
  remaining: number;
  issues: Issue[];
}

type SubscriptionTab = "suggested" | "subscribed";

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubscriptionChange?: () => void;
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
  isOpen,
  onClose,
  onSubscriptionChange,
}) => {
  const { currentUser } = useUser();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<SubscriptionTab>("suggested");
  const [subscribedIssues, setSubscribedIssues] = useState<Issue[]>([]);
  const [suggestedIssues, setSuggestedIssues] = useState<Issue[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !currentUser) {
      return;
    }

    let isMounted = true;

    const loadData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const subscriptionsRes = await api.get<SubscriptionResponse>(
          apiRoutes.user.subscriptionsByUserId(currentUser.id),
          {
            params: {
              limit: 500,
              sortOrder: "DESC",
            },
          }
        );

        const relations = Array.isArray(subscriptionsRes.data?.relations)
          ? subscriptionsRes.data.relations
          : [];

        const subscribedIds = relations
          .filter(
            (relation) =>
              relation.rel_type === "subscription" &&
              relation.rel_node_type === "issue" &&
              (relation.properties?.value ?? 1) >= 0
          )
          .map((relation) => relation.rel_node_id);

        const subscribedIssueSet = new Set(subscribedIds);

        const subscribedRes = subscribedIds.length
          ? await api.get<Issue[]>(apiRoutes.issues.root, {
              params: {
                issueIds: JSON.stringify(subscribedIds),
                limit: subscribedIds.length,
                sortField: "created_at",
                sortOrder: "DESC",
              },
            })
          : { data: [] as Issue[] };

        const subscribedIssuesList = Array.isArray(subscribedRes.data)
          ? subscribedRes.data
          : [];

        const rankingsResponse = await getIssueRankings();
        const rankings = Array.isArray(rankingsResponse.data)
          ? rankingsResponse.data
          : [];

        const rankedIssues = rankings
          .map((ranking: IssueRanking) => ranking.issue)
          .filter((issue): issue is Issue => Boolean(issue));

        const rankedSuggested = rankedIssues.filter(
          (issue) => issue && !subscribedIssueSet.has(issue.id)
        );

        const issuePages: Issue[] = [];
        let remaining = 1;
        let offset = 0;
        const limit = 100;
        let pageCount = 0;

        while (remaining > 0 && pageCount < 20) {
          const issuesRes = await api.get<IssuesCountResponse>(apiRoutes.issues.root, {
            params: {
              count: true,
              limit,
              offset,
              sortField: "created_at",
              sortOrder: "DESC",
            },
          });

          const payload = issuesRes.data;
          if (!payload || !Array.isArray(payload.issues)) {
            break;
          }

          issuePages.push(...payload.issues);
          remaining = payload.remaining;
          offset += payload.issues.length;
          pageCount += 1;

          if (payload.issues.length === 0) {
            break;
          }
        }

        const rankedSuggestedIds = new Set(rankedSuggested.map((issue) => issue.id));
        const otherSuggested = issuePages.filter(
          (issue) =>
            !subscribedIssueSet.has(issue.id) && !rankedSuggestedIds.has(issue.id)
        );

        const suggestedSubjectOnly = [...rankedSuggested, ...otherSuggested].filter(
          isSubjectOnly
        );

        if (isMounted) {
          setSubscribedIssues(subscribedIssuesList);
          setSuggestedIssues(suggestedSubjectOnly);
        }
      } catch (err) {
        if (isMounted) {
          setError("Unable to load subscriptions.");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadData();
    return () => {
      isMounted = false;
    };
  }, [currentUser, isOpen]);

  /** Subject-only: exclude compound/influence issues (those with topic_cname set) */
  const isSubjectOnly = (issue: Issue): boolean =>
    issue.topic_cname == null || issue.topic_cname === "";

  const handleSubscriptionUpdate = (issue: Issue, subscribed: boolean, level?: number) => {
    const isUnsubscribed = level === -1 || (subscribed === false && level === undefined);

    setSubscribedIssues((prev) => {
      if (!isUnsubscribed) {
        if (prev.some((existing) => existing.id === issue.id)) {
          return prev;
        }
        return [issue, ...prev];
      }
      return prev.filter((existing) => existing.id !== issue.id);
    });

    setSuggestedIssues((prev) => {
      if (!isUnsubscribed) {
        return prev.filter((existing) => existing.id !== issue.id);
      }
      if (prev.some((existing) => existing.id === issue.id)) {
        return prev;
      }
      if (!isSubjectOnly(issue)) {
        return prev;
      }
      return [issue, ...prev];
    });

    onSubscriptionChange?.();
  };

  const renderIssueRow = (issue: Issue) => (
    <div className="stories-subscription-row flex items-center justify-between gap-3">
      <EntityAvatar
        entity={issue}
        entityType="issue"
        entityChip
        showCloseChip={false}
        showLink
        className="min-w-[160px] max-w-[260px] text-xs pr-0"
      />
      <div
        className="stories-subscription-action flex items-center justify-center"
        onClick={(event) => event.stopPropagation()}
      >
        <EntitySubscription
          entityId={issue.id}
          entityType="issue"
          variant="mini"
          showLabel={false}
          onSubscriptionChange={(subscribed, level) =>
            handleSubscriptionUpdate(issue, subscribed, level)
          }
        />
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Manage subscriptions"
      bodyDisplayStyle="block"
    >
      <div className="stories-modal-tabs pt-2">
        <ScrollableTabsList
          tabs={[
            { label: "Suggested", value: "suggested" },
            { label: "Subscribed", value: "subscribed" },
          ]}
          navlink={false}
          selectedTab={activeTab}
          onTabChange={(value) => setActiveTab(value as SubscriptionTab)}
          pageTitle="Subscriptions"
          className="text-base"
        />
      </div>
      <div className="stories-modal-content mt-4 flex flex-col gap-3">
        {isLoading && (
          <div className="stories-modal-loading flex flex-col gap-2">
            <LoadingSkeleton variant="entity-avatar" avatarSize="h-9 w-9" count={10} />
          </div>
        )}
        {!isLoading && error && (
          <div className="stories-modal-error text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
            {error}
          </div>
        )}
        {!isLoading && !error && activeTab === "subscribed" && (
          <div className="stories-subscribed-list flex flex-col gap-2">
            {subscribedIssues.length === 0 && (
              <div className="stories-modal-empty text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                <EmptyState
                  title="No subscriptions"
                  subtitle="You haven't subscribed to any issues yet."
                  icon={<Bell className="size-12" />}
                />
              </div>
            )}
            {subscribedIssues.map((issue) => (
              <div
                key={`subscribed-${issue.id}`}
                className="stories-subscription-item rounded-lg hover:bg-eoe-icon dark:hover:bg-eoe-dark-icon p-1 cursor-pointer"
                onClick={() => navigate(`/issue/${issue.cname}`)}
              >
                {renderIssueRow(issue)}
              </div>
            ))}
          </div>
        )}
        {!isLoading && !error && activeTab === "suggested" && (
          <div className="stories-suggested-list flex flex-col gap-2">
            {suggestedIssues.length === 0 && (
              <div className="stories-modal-empty text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                No suggestions available right now.
              </div>
            )}
            {suggestedIssues.map((issue) => (
              <div
                key={`suggested-${issue.id}`}
                className="stories-suggested-item rounded-lg hover:bg-eoe-icon dark:hover:bg-eoe-dark-icon p-1 cursor-pointer"
                onClick={() => navigate(`/issue/${issue.cname}`)}
              >
                {renderIssueRow(issue)}
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default SubscriptionModal;
